import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ArrowRight } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useWorkflowLifecycleStore } from '@/stores/workflow-lifecycle-store'
import type { WorkflowVersion } from '@/schemas/workflow'
import { DiffCanvas } from './diff-canvas'

interface VersionDiffDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  workflowId: string
  fromVersion: number | null
  toVersion: number | null
  versions: WorkflowVersion[]
  onChangeVersions: (from: number, to: number) => void
  onRestore: (version: number) => void
  onExport: (version: number) => void
}

export function VersionDiffDrawer({
  open,
  onOpenChange,
  projectId,
  workflowId,
  fromVersion,
  toVersion,
  versions,
  onChangeVersions,
  onRestore,
  onExport,
}: VersionDiffDrawerProps) {
  const { t } = useTranslation('workflows')
  const { diffCache, diffLoading, diffError, fetchDiff } =
    useWorkflowLifecycleStore()

  const diffKey =
    fromVersion != null && toVersion != null
      ? `${workflowId}:${fromVersion}->${toVersion}`
      : null
  const diff = diffKey ? diffCache[diffKey] : undefined

  useEffect(() => {
    if (!open) return
    if (fromVersion == null || toVersion == null) return
    if (diff) return
    fetchDiff(projectId, workflowId, fromVersion, toVersion)
  }, [open, projectId, workflowId, fromVersion, toVersion, diff, fetchDiff])

  const summaryText = useMemo(() => {
    if (!diff || fromVersion == null || toVersion == null) return null
    const edges =
      diff.summary.added_edges +
      diff.summary.removed_edges +
      diff.summary.modified_edges
    return t('lifecycle.diff.summary', {
      added: diff.summary.added_nodes,
      removed: diff.summary.removed_nodes,
      modified: diff.summary.modified_nodes,
      edges,
    })
  }, [diff, fromVersion, toVersion, t])

  const chips = useMemo(() => {
    if (!diff) return null
    const edges =
      diff.summary.added_edges +
      diff.summary.removed_edges +
      diff.summary.modified_edges
    return [
      {
        key: 'added',
        label: `+${diff.summary.added_nodes}`,
        tone: 'added' as const,
        suffix: 'adicionado',
      },
      {
        key: 'removed',
        label: `−${diff.summary.removed_nodes}`,
        tone: 'removed' as const,
        suffix: 'removido',
      },
      {
        key: 'modified',
        label: `~${diff.summary.modified_nodes}`,
        tone: 'modified' as const,
        suffix: 'modificado',
      },
      {
        key: 'edges',
        label: `⇢ ${edges}`,
        tone: 'edges' as const,
        suffix: 'arestas',
      },
    ]
  }, [diff])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(960px,100vw-48px)] sm:max-w-none p-0 flex flex-col"
      >
        <SheetHeader className="px-8 pt-7 pb-5 border-b border-border/80">
          <SheetTitle className="sr-only">Comparar versões</SheetTitle>
          <SheetDescription className="sr-only">
            Comparação entre duas versões do workflow
          </SheetDescription>

          <div className="flex flex-col gap-2">
            <span className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground/70">
              Comparando
            </span>
            <div className="flex items-center gap-5 flex-wrap">
              <VersionSelect
                label="De"
                value={fromVersion}
                options={versions}
                onChange={(from) => {
                  if (toVersion != null) onChangeVersions(from, toVersion)
                }}
              />
              <ArrowRight
                className="w-6 h-6 text-muted-foreground/40"
                aria-hidden
                style={{
                  animation: open ? 'slideRight 600ms ease-out' : undefined,
                }}
              />
              <VersionSelect
                label="Para"
                value={toVersion}
                options={versions}
                onChange={(to) => {
                  if (fromVersion != null) onChangeVersions(fromVersion, to)
                }}
                accent
              />
            </div>
          </div>
        </SheetHeader>

        {/* Summary chip row */}
        <div
          data-testid="diff-summary-banner"
          className="flex items-center gap-2 px-8 py-3 border-b border-border/60 min-h-[52px] flex-wrap"
        >
          {fromVersion != null && toVersion != null && (
            <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground/60 mr-2 tabular-nums">
              De v{fromVersion} para v{toVersion}
            </span>
          )}
          {diffLoading && !chips && (
            <span className="text-xs text-muted-foreground">Calculando…</span>
          )}
          {chips?.map((c) => (
            <SummaryChip key={c.key} label={c.label} suffix={c.suffix} tone={c.tone} />
          ))}
          {/* Screen-reader-only full sentence */}
          {summaryText && <span className="sr-only">{summaryText}</span>}
        </div>

        {/* Body */}
        <div className="flex-1 relative overflow-hidden">
          {diffLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {diffError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('lifecycle.diff.loadFailed')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (fromVersion != null && toVersion != null) {
                    fetchDiff(projectId, workflowId, fromVersion, toVersion)
                  }
                }}
              >
                {t('lifecycle.diff.retry')}
              </Button>
            </div>
          )}
          {diff && !diffLoading && !diffError && <DiffCanvas diff={diff} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/80">
          {toVersion != null && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport(toVersion)}
            >
              Exportar v{toVersion}
            </Button>
          )}
          {toVersion != null && (
            <Button
              variant="action-indigo"
              size="sm"
              onClick={() => onRestore(toVersion)}
            >
              Restaurar v{toVersion} como rascunho
            </Button>
          )}
        </div>

        <style>{`
          @keyframes slideRight {
            0%   { transform: translateX(-12px); opacity: 0; }
            100% { transform: translateX(0);     opacity: 1; }
          }
        `}</style>
      </SheetContent>
    </Sheet>
  )
}

interface VersionSelectProps {
  label: string
  value: number | null
  options: WorkflowVersion[]
  onChange: (v: number) => void
  accent?: boolean
}

function VersionSelect({
  label,
  value,
  options,
  onChange,
  accent,
}: VersionSelectProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground/60">
        {label}
      </label>
      <Select
        value={value?.toString() ?? ''}
        onValueChange={(v) => {
          const n = Number(v)
          if (!Number.isNaN(n)) onChange(n)
        }}
      >
        <SelectTrigger
          className={cn(
            'h-auto gap-2 border-0 bg-transparent p-0',
            'font-mono tabular-nums tracking-tighter leading-none',
            'text-[44px] font-semibold',
            'hover:bg-transparent focus:ring-0 focus:outline-none',
            accent ? 'text-foreground' : 'text-muted-foreground/90',
            '[&>svg]:w-4 [&>svg]:h-4 [&>svg]:opacity-40 [&>svg]:mt-3'
          )}
        >
          <SelectValue>
            {value != null ? (
              <>
                <span className="text-muted-foreground/50 text-[22px] align-top mr-0.5">
                  v
                </span>
                {value}
              </>
            ) : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((v) => (
            <SelectItem
              key={v.version_number}
              value={v.version_number.toString()}
            >
              v{v.version_number}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

type ChipTone = 'added' | 'removed' | 'modified' | 'edges'

function SummaryChip({
  label,
  suffix,
  tone,
}: {
  label: string
  suffix: string
  tone: ChipTone
}) {
  const styles: Record<ChipTone, { bg: string; text: string; border: string }> =
    {
      added: {
        bg: 'hsl(var(--diff-added) / 0.12)',
        text: 'hsl(var(--diff-added))',
        border: 'hsl(var(--diff-added) / 0.3)',
      },
      removed: {
        bg: 'hsl(var(--diff-removed) / 0.12)',
        text: 'hsl(var(--diff-removed))',
        border: 'hsl(var(--diff-removed) / 0.3)',
      },
      modified: {
        bg: 'hsl(var(--diff-modified) / 0.12)',
        text: 'hsl(var(--diff-modified))',
        border: 'hsl(var(--diff-modified) / 0.3)',
      },
      edges: {
        bg: 'hsl(var(--muted) / 0.4)',
        text: 'hsl(var(--muted-foreground))',
        border: 'hsl(var(--border))',
      },
    }
  const s = styles[tone]
  return (
    <span
      className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium tabular-nums border"
      style={{
        backgroundColor: s.bg,
        color: s.text,
        borderColor: s.border,
      }}
    >
      <span className="font-mono font-semibold">{label}</span>
      <span className="text-[10px] opacity-75 uppercase tracking-wider">
        {suffix}
      </span>
    </span>
  )
}
